"use client";

import * as React from "react";

import { InsightDeck } from "@/components/site/sections/overview-board/insight-deck";
import { OverviewItemWindow } from "@/components/site/sections/overview-board/item-window";
import { RiskHeader } from "@/components/site/sections/overview-board/risk-header";
import { RoadmapBoard } from "@/components/site/sections/overview-board/roadmap-board";
import {
  findItem,
  overviewDocument,
  type OverviewDocument,
} from "@/lib/overview-document";
import { cn } from "@/lib/utils";

/**
 * The item window's state for one board. Focus goes back to the card of the
 * last item shown when it is visible, otherwise to whatever opened the window.
 */
function useItemWindow(
  document: OverviewDocument,
  rootRef: React.RefObject<HTMLElement | null>,
) {
  const [openItemId, setOpenItemId] = React.useState<string | null>(null);
  const openerRef = React.useRef<Element | null>(null);
  const lastItemRef = React.useRef<string | null>(null);

  const open = React.useCallback(
    (id: string) => {
      if (!findItem(document, id)) {
        return;
      }

      openerRef.current = globalThis.document.activeElement;
      lastItemRef.current = id;
      setOpenItemId(id);
    },
    [document],
  );

  const navigate = React.useCallback((id: string) => {
    lastItemRef.current = id;
    setOpenItemId(id);
  }, []);

  const restoreFocus = React.useCallback(() => {
    const trigger = lastItemRef.current
      ? rootRef.current?.querySelector<HTMLElement>(
          `[data-overview-item-trigger="${lastItemRef.current}"]`,
        )
      : null;
    const target =
      trigger && trigger.offsetParent !== null && !trigger.closest("[inert]")
        ? trigger
        : openerRef.current;

    if (target instanceof HTMLElement && target.isConnected) {
      target.focus();
    }
  }, [rootRef]);

  const itemWindow = (
    <OverviewItemWindow
      document={document}
      itemId={openItemId}
      onNavigate={navigate}
      onClose={() => setOpenItemId(null)}
      onRestoreFocus={restoreFocus}
    />
  );

  return { open, itemWindow };
}

/**
 * The B6 Overview for the Demo Team, seen by its lead Adrian Cole: risk,
 * the issues / opportunities / strengths folder, the roadmap and the item
 * window.
 */
export function OverviewBoard({
  size,
  lanesBleedClassName,
}: {
  size: "compact" | "expanded";
  lanesBleedClassName?: string;
}) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const { open, itemWindow } = useItemWindow(overviewDocument, rootRef);

  return (
    <div
      ref={rootRef}
      className={cn("flex flex-col", size === "compact" ? "gap-7 sm:gap-8" : "gap-8 sm:gap-10")}
    >
      <RiskHeader document={overviewDocument} headingLevel={2} size={size} />
      <InsightDeck document={overviewDocument} headingLevel={2} onOpen={open} />
      <RoadmapBoard
        document={overviewDocument}
        headingLevel={2}
        lanesBleedClassName={lanesBleedClassName}
        // In the compact frame this row lands under the fullscreen button.
        toolbarClassName={size === "compact" ? "sm:pr-16" : undefined}
        onOpen={open}
      />
      {itemWindow}
    </div>
  );
}

/** The same roadmap and step window on its own, for sections that already explain the overview. */
export function OverviewRoadmapBoard({
  headingLevel,
  lanesBleedClassName,
}: {
  headingLevel: 2 | 3 | 4;
  lanesBleedClassName?: string;
}) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const { open, itemWindow } = useItemWindow(overviewDocument, rootRef);

  return (
    <div ref={rootRef}>
      <RoadmapBoard
        document={overviewDocument}
        headingLevel={headingLevel}
        lanesBleedClassName={lanesBleedClassName}
        onOpen={open}
      />
      {itemWindow}
    </div>
  );
}
